import React from 'react';

import { Download as DownloadIcon } from '@mui/icons-material';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';

import { noop } from '@clearverify/utils';

import {
  useGetEmployersListForVerificationRequestForMyVerifyQuery,
  VerificationRequestType,
} from 'src/api';
import { PdfRenderer } from 'src/components/common';
import { SelectField } from 'src/components/finalForm';
import { ModalId } from 'src/constants/modals';
import { useScreenInfo } from 'src/hooks/common';
import { useModalV2 } from 'src/hooks/shared';
import { GetWorkLetterModal } from 'src/modals/position';

import { MY_VERIFY_PDF_DOCUMENT } from './constants';
import { EmployeeMyVerifyFormForAnotherRequestProps, StatusForm } from './types';
import { VERIFICATION_TYPES_OPTIONS } from './utils';

export const EmployeeMyVerifyFormForAnotherRequest: React.FC<
  EmployeeMyVerifyFormForAnotherRequestProps
> = ({ verificationType, setStatusForm, setVerificationType }) => {
  const { isMobile } = useScreenInfo();
  const { openModal } = useModalV2();

  const { data: employersData, loading: employersDataLoading } =
    useGetEmployersListForVerificationRequestForMyVerifyQuery();

  const handleGenerateWorkLetter = React.useCallback(() => {
    if (employersData?.getEmployersListForVerificationRequestForMyVerify.employers.length === 0) {
      return;
    }
    openModal(GetWorkLetterModal, {
      id: ModalId.GetWorkLetter,
    });
  }, [employersData?.getEmployersListForVerificationRequestForMyVerify.employers, openModal]);

  const handleChangePdfType = React.useCallback(
    (value: VerificationRequestType) => {
      setVerificationType(value);
    },
    [setVerificationType],
  );

  const hasNoEmploymentData =
    !employersDataLoading &&
    employersData?.getEmployersListForVerificationRequestForMyVerify.employers.length === 0;

  return (
    <Grid container wrap="nowrap" p={isMobile ? 1 : 4} pt={isMobile ? 2 : 4} gap={3}>
      <Grid container>
        <Grid size={isMobile ? 12 : 5}>
          <Typography variant="h5" paddingBottom={1}>
            Well done!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            The Verification has been sent. You can now see this item in your inbox.
          </Typography>
        </Grid>

        {!isMobile && (
          <Grid container gap={7} size={7}>
            <Grid container direction="row-reverse" size={12}>
              <Grid maxWidth="300px" width="100%">
                <SelectField
                  fullWidth
                  input={{
                    name: 'Verification Type',
                    value: verificationType,
                    onChange: handleChangePdfType,
                    onBlur: noop,
                    onFocus: noop,
                  }}
                  meta={{}}
                  label="Example Verification Type"
                  options={VERIFICATION_TYPES_OPTIONS}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid container mt={4} spacing={isMobile ? 0 : 3}>
          <Grid container height="fit-content" size={isMobile ? 12 : 5}>
            <Grid size={12}>
              <Box display="flex" gap={3} justifyContent="center">
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => setStatusForm(StatusForm.request)}
                >
                  Send another verification
                </Button>
              </Box>
            </Grid>
            <Grid paddingTop={4} size={12}>
              <Divider>or</Divider>
              <Box display="flex" gap={3} justifyContent="center" mt={4}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  disabled={hasNoEmploymentData}
                  loading={employersDataLoading}
                  endIcon={<DownloadIcon />}
                  onClick={handleGenerateWorkLetter}
                >
                  Get Work Letter
                </Button>
              </Box>
            </Grid>
          </Grid>
          {!isMobile && (
            <Grid container gap={7} size={7}>
              <Grid height="100%" maxHeight="725px" size={12}>
                <PdfRenderer renderMode="canvas" src={MY_VERIFY_PDF_DOCUMENT[verificationType]} />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
